import React, { useState, useEffect } from 'react';
import { Button, TextInput, Textarea, Modal, Loader } from '@mantine/core';
import apiService from '../../services/api';
import TrainerEarningsDashboard from './TrainerEarningsDashboard';
import type { TrainerPortalProps } from '../../types';

interface TrainerRequest {
  id: string;
  status: string;
  client_username?: string;
  client_name?: string;
  pet_name?: string;
  pet_breed?: string;
  pet_age?: string;
  explanation?: string;
  duration_minutes?: number;
  total_price?: number;
  requested_at: string;
  accepted_at?: string;
  payment_status?: string;
  [key: string]: unknown;
}

interface DeliverablesFormData {
  summary?: string;
  checklist_items?: string[];
  [key: string]: unknown;
}

interface EditFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  address: string;
  business_name: string;
  time_zone: string;
  business_street: string;
  business_apt: string;
  business_city: string;
  business_state: string;
  business_zip: string;
  business_address: string;
  credentials: string[];
  training_philosophy: string[];
  areas_of_expertise: string[];
  specialties: string[];
  experience_years: string;
  year_began_training: string;
  first_10_min_rate: string;
  per_minute_rate: string;
  bio: string;
  legal_states: string[];
  current_rating: number;
  hourly_rate?: string;
  state?: string;
  zip_code?: string;
  [key: string]: unknown;
}

interface ConnectStatusExtended {
  has_connect_account?: boolean;
  onboarding_complete?: boolean;
  Code?: number;
  Message?: string;
  url?: string;
  [key: string]: unknown;
}

interface StateOption {
  value: string;
  label: string;
}

const getStatusClasses = (status: string): string => {
  switch (status) {
    case 'pending':
    case 'awaiting_payment':
      return 'bg-[#fff3cd] text-[#856404] border border-[#ffeaa7]';
    case 'accepted':
    case 'active':
      return 'bg-[#d4edda] text-[#155724] border border-[#c3e6cb]';
    case 'declined':
      return 'bg-[#f8d7da] text-[#721c24] border border-[#f5c6cb]';
    case 'cancelled':
      return 'bg-[#e2e3e5] text-[#383d41] border border-[#d6d8db]';
    default:
      return 'bg-[#e9ecef] text-[#495057] border border-[#dee2e6]';
  }
};

function TrainerPortal({ onBackToHome, onLogout, isLoggedIn }: TrainerPortalProps): React.ReactElement {
  console.log('=== TRAINER PORTAL COMPONENT RENDERED ===');
  console.log('Props:', { onBackToHome, onLogout, isLoggedIn });
  console.log('localStorage trainerId:', localStorage.getItem('trainerId'));
  console.log('localStorage username:', localStorage.getItem('username'));
  console.log('API Service available:', !!apiService);
  console.log('API Service methods:', Object.keys(apiService));

  const [activeTab, setActiveTab] = useState<string>('profile');
  const [trainerProfile, setTrainerProfile] = useState<Record<string, unknown> | null>(null);
  const [activeTrainingRequests, setActiveTrainingRequests] = useState<TrainerRequest[]>([]);
  const [sessionHistory, setSessionHistory] = useState<TrainerRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address: '',
    business_name: '',
    time_zone: '',
    business_street: '',
    business_apt: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    business_address: '',
    credentials: [],
    training_philosophy: [],
    areas_of_expertise: [],
    specialties: [],
    experience_years: '',
    year_began_training: '',
    first_10_min_rate: '',
    per_minute_rate: '',
    bio: '',
    legal_states: [],
    current_rating: 0.0
  });
  const [acceptingRequests, setAcceptingRequests] = useState<boolean>(false);
  const [toggleLoading, setToggleLoading] = useState<boolean>(false);
  const [trainerRequests, setTrainerRequests] = useState<TrainerRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState<boolean>(false);
  const [deliverablesForms, setDeliverablesForms] = useState<Record<string, boolean>>({});
  const [deliverablesData, setDeliverablesData] = useState<Record<string, DeliverablesFormData>>({});
  const [savingDeliverables, setSavingDeliverables] = useState<Record<string, boolean>>({});
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
  const [selectedDeliverablesRequest, setSelectedDeliverablesRequest] = useState<TrainerRequest | null>(null);
  const [connectStatus, setConnectStatus] = useState<ConnectStatusExtended | null>(null);
  const [connectLoading, setConnectLoading] = useState<boolean>(false);

  const credentialOptions: string[] = [
    'Formal degree',
    'Professional Dog Trainer School',
    'International Association of Canine Professionals',
    'Prong Collar Assessment',
    'CDT',
    'CDTA',
    'Certification Council of Canine Dog Trainers',
    'CPDT-KA',
    'CPDT-KSA',
    'CBCC-KA',
    'CBCC-KSA',
    'NADOI \u2013 Certified Member',
    'Puppy',
    'Novice Competition',
    'Open Competition',
    'Utility Competition',
    'Tracking',
    'Tracking Excellent',
    'Agility',
    'Agility Excellent',
    'Karen Prior Academy',
    'CDT',
    'Association of Animal Behavior Professionals',
    'CDBT',
    'Certified Fear Free',
    'Stephan Lynsay LIMA',
    'Grisha Stewart CBATI',
    'AKC Judge',
    'Fetch',
    'Farm Dog',
    'Obedience',
    'Rally',
    'Scentwork',
    'Herding',
    'Agility',
    'AKC Canine Good Citizen Evaluator',
    'Canine Fitness Instructor',
    'CCFT \u2013 list affiliation',
    'Do More With Your Dogs',
    'CRDI',
    'CCFC',
    'SDJ',
    'AACE',
    'Other sport judge',
    'Breed expert'
  ];

  const trainingPhilosophyOptions: string[] = [
    'Force Free / Pure Positive',
    'Positive Reinforcement Based (+R)',
    'Clicker / Marker Training',
    'Fear Free',
    'Science Based',
    'Relationship Based',
    'Balanced',
    'LIMA (Least Intrusive, Minimally Aversive)',
    'Traditional Training Methods',
    'Alpha / Pack Based',
    'Remote Training'
  ];

  const areasOfExpertiseOptions: string[] = [
    'Puppy',
    'Manners',
    'Behavior Modification',
    'Aggression Management \u2013 Dog to dog',
    'Aggression Management - human directed',
    'Reactivity',
    'Separation Anxiety',
    'Anxiety / Fears / Phobias',
    'Leash Walking',
    'Potty Training',
    'Rescue Rehab',
    'Service Dog',
    'Therapy Dog',
    'Protection',
    'Tracking',
    'Competition Obedience',
    'Scentwork',
    'Agility',
    'Conformation',
    'Herding',
    'Earthdog'
  ];

  const timeZoneOptions: string[] = [
    'Pacific Time (PT)',
    'Mountain Time (MT)',
    'Central Time (CT)',
    'Eastern Time (ET)',
    'Alaska Time (AKT)',
    'Hawaii Time (HST)'
  ];

  const stateOptions: StateOption[] = [
    { value: 'AL', label: 'Alabama (AL)' },
    { value: 'AK', label: 'Alaska (AK)' },
    { value: 'AZ', label: 'Arizona (AZ)' },
    { value: 'AR', label: 'Arkansas (AR)' },
    { value: 'CA', label: 'California (CA)' },
    { value: 'CO', label: 'Colorado (CO)' },
    { value: 'CT', label: 'Connecticut (CT)' },
    { value: 'DE', label: 'Delaware (DE)' },
    { value: 'FL', label: 'Florida (FL)' },
    { value: 'GA', label: 'Georgia (GA)' },
    { value: 'HI', label: 'Hawaii (HI)' },
    { value: 'ID', label: 'Idaho (ID)' },
    { value: 'IL', label: 'Illinois (IL)' },
    { value: 'IN', label: 'Indiana (IN)' },
    { value: 'IA', label: 'Iowa (IA)' },
    { value: 'KS', label: 'Kansas (KS)' },
    { value: 'KY', label: 'Kentucky (KY)' },
    { value: 'LA', label: 'Louisiana (LA)' },
    { value: 'ME', label: 'Maine (ME)' },
    { value: 'MD', label: 'Maryland (MD)' },
    { value: 'MA', label: 'Massachusetts (MA)' },
    { value: 'MI', label: 'Michigan (MI)' },
    { value: 'MN', label: 'Minnesota (MN)' },
    { value: 'MS', label: 'Mississippi (MS)' },
    { value: 'MO', label: 'Missouri (MO)' },
    { value: 'MT', label: 'Montana (MT)' },
    { value: 'NE', label: 'Nebraska (NE)' },
    { value: 'NV', label: 'Nevada (NV)' },
    { value: 'NH', label: 'New Hampshire (NH)' },
    { value: 'NJ', label: 'New Jersey (NJ)' },
    { value: 'NM', label: 'New Mexico (NM)' },
    { value: 'NY', label: 'New York (NY)' },
    { value: 'NC', label: 'North Carolina (NC)' },
    { value: 'ND', label: 'North Dakota (ND)' },
    { value: 'OH', label: 'Ohio (OH)' },
    { value: 'OK', label: 'Oklahoma (OK)' },
    { value: 'OR', label: 'Oregon (OR)' },
    { value: 'PA', label: 'Pennsylvania (PA)' },
    { value: 'RI', label: 'Rhode Island (RI)' },
    { value: 'SC', label: 'South Carolina (SC)' },
    { value: 'SD', label: 'South Dakota (SD)' },
    { value: 'TN', label: 'Tennessee (TN)' },
    { value: 'TX', label: 'Texas (TX)' },
    { value: 'UT', label: 'Utah (UT)' },
    { value: 'VT', label: 'Vermont (VT)' },
    { value: 'VA', label: 'Virginia (VA)' },
    { value: 'WA', label: 'Washington (WA)' },
    { value: 'WV', label: 'West Virginia (WV)' },
    { value: 'WI', label: 'Wisconsin (WI)' },
    { value: 'WY', label: 'Wyoming (WY)' }
  ];

  useEffect(() => {
    console.log('=== TRAINER PORTAL USEEFFECT TRIGGERED ===');
    console.log('Component mounted, calling loadInitialData...');
    loadInitialData();
  }, []);

  const loadInitialData = async (): Promise<void> => {
    console.log('=== LOADING INITIAL DATA ===');
    await Promise.all([
      loadTrainerProfile(),
      loadActiveTrainingRequests(),
      loadSessionHistory(),
      loadTrainerAvailability()
    ]);
    console.log('=== INITIAL DATA LOAD COMPLETE ===');
  };

  const loadTrainerAvailability = async (): Promise<void> => {
    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) return;
    try {
      const response = await apiService.getTrainerAvailability(trainerId) as Record<string, unknown>;
      if (response.Code === 200) {
        const isActive = response.is_active as boolean;
        setAcceptingRequests(isActive);
        if (isActive) {
          await loadTrainerRequests();
        }
      }
    } catch (err) {
      console.error('Error loading trainer availability:', err);
    }
  };

  const loadTrainerRequests = async (): Promise<void> => {
    try {
      setRequestsLoading(true);
      const trainerId = localStorage.getItem('trainerId');
      console.log('Loading trainer requests for ID:', trainerId);

      if (trainerId) {
        const response = await apiService.getTrainerRequests(trainerId) as Record<string, unknown>;
        console.log('Trainer requests response:', response);

        if (response.Code === 200) {
          const requests = (response.requests as TrainerRequest[]) || [];
          setTrainerRequests(requests);

          for (const request of requests) {
            if (request.status === 'completed') {
              try {
                const deliverablesResponse = await apiService.getDeliverables(request.id) as Record<string, unknown>;
                if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                  setDeliverablesData(prev => ({
                    ...prev,
                    [request.id]: deliverablesResponse.data as DeliverablesFormData
                  }));
                }
              } catch (err) {
                console.log(`No deliverables found for request ${request.id}`);
              }
            }
          }
        } else {
          console.error('Failed to load trainer requests:', response.Message);
          setError(`Failed to load trainer requests: ${response.Message}`);
        }
      } else {
        console.error('No trainer ID found in localStorage');
        setError('No trainer ID found. Please complete your profile setup.');
      }
    } catch (err) {
      console.error('Error loading trainer requests:', err);
      setError('Failed to load trainer requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleSaveDeliverables = async (requestId: string, request: TrainerRequest): Promise<void> => {
    try {
      setSavingDeliverables(prev => ({ ...prev, [requestId]: true }));
      const trainerId = localStorage.getItem('trainerId');
      const formData = deliverablesData[requestId] || {};

      const checklistItems = (formData.checklist_items || [])
        .filter((item: string) => item && item.trim().length > 0)
        .map((item: string) => item.trim());

      const response = await apiService.createOrUpdateDeliverables({
        session_request_id: requestId,
        trainer_id: trainerId,
        summary: formData.summary || '',
        checklist_items: checklistItems
      }) as Record<string, unknown>;

      if (response.Code === 200) {
        const deliverablesResponse = await apiService.getDeliverables(requestId) as Record<string, unknown>;
        if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
          setDeliverablesData(prev => ({
            ...prev,
            [requestId]: deliverablesResponse.data as DeliverablesFormData
          }));
        }
        setDeliverablesForms(prev => ({ ...prev, [requestId]: false }));
        alert('Deliverables saved successfully!');
      } else {
        alert(`Failed to save deliverables: ${response.Message}`);
      }
    } catch (err) {
      console.error('Error saving deliverables:', err);
      alert('Failed to save deliverables');
    } finally {
      setSavingDeliverables(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDeliverablesInputChange = (requestId: string, field: string, value: string | string[]): void => {
    setDeliverablesData(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [field]: value
      }
    }));
  };

  const handleAddChecklistItem = (requestId: string): void => {
    setDeliverablesData(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        checklist_items: [...(prev[requestId]?.checklist_items || []), '']
      }
    }));
  };

  const handleRemoveChecklistItem = (requestId: string, index: number): void => {
    setDeliverablesData(prev => {
      const items = [...(prev[requestId]?.checklist_items || [])];
      items.splice(index, 1);
      return {
        ...prev,
        [requestId]: {
          ...prev[requestId],
          checklist_items: items
        }
      };
    });
  };

  const handleChecklistItemChange = (requestId: string, index: number, value: string): void => {
    setDeliverablesData(prev => {
      const items = [...(prev[requestId]?.checklist_items || [])];
      items[index] = value;
      return {
        ...prev,
        [requestId]: {
          ...prev[requestId],
          checklist_items: items
        }
      };
    });
  };

  const loadTrainerProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      const trainerId = localStorage.getItem('trainerId');
      console.log('=== LOADING TRAINER PROFILE ===');
      console.log('Loading trainer profile for ID:', trainerId);

      if (trainerId) {
        const response = await apiService.getTrainerProfile(trainerId) as Record<string, unknown>;
        console.log('Trainer profile response:', response);

        if (response.Code === 200) {
          const data = response.data as Record<string, unknown>;
          console.log('Setting trainer profile data:', data);
          setTrainerProfile(data);

          const parseBusinessAddress = (address: string | undefined): { street: string; apt: string; city: string; state: string; zip: string } => {
            if (!address) return { street: '', apt: '', city: '', state: '', zip: '' };
            const parts = address.split(',').map((p: string) => p.trim());
            if (parts.length >= 3) {
              return {
                street: parts[0] || '',
                apt: parts[1]?.includes('Apt') || parts[1]?.includes('Ste') || parts[1]?.includes('Unit') ? parts[1] : '',
                city: parts[parts.length - 2] || '',
                state: parts[parts.length - 1]?.match(/^([A-Z]{2})\s*(\d{5})$/)?.[1] || '',
                zip: parts[parts.length - 1]?.match(/^([A-Z]{2})\s*(\d{5})$/)?.[2] || ''
              };
            }
            return { street: '', apt: '', city: '', state: '', zip: '' };
          };

          const businessAddressParts = parseBusinessAddress(data.business_address as string | undefined);

          const newEditFormData: EditFormData = {
            first_name: (data.first_name as string) || '',
            last_name: (data.last_name as string) || '',
            email: (data.email as string) || '',
            phone_number: (data.phone_number as string) || '',
            address: (data.address as string) || '',
            business_name: (data.business_name as string) || '',
            time_zone: (data.time_zone as string) || '',
            business_street: businessAddressParts.street,
            business_apt: businessAddressParts.apt,
            business_city: businessAddressParts.city,
            business_state: businessAddressParts.state,
            business_zip: businessAddressParts.zip,
            business_address: (data.business_address as string) || '',
            credentials: Array.isArray(data.credentials) ? data.credentials as string[] :
                          data.credentials ? (data.credentials as string).split(',').map((c: string) => c.trim()) : [],
            training_philosophy: Array.isArray(data.training_philosophy) ? data.training_philosophy as string[] :
                               data.training_philosophy ? (data.training_philosophy as string).split(',').map((p: string) => p.trim()) : [],
            areas_of_expertise: Array.isArray(data.areas_of_expertise) ? data.areas_of_expertise as string[] :
                               data.areas_of_expertise ? (data.areas_of_expertise as string).split(',').map((a: string) => a.trim()) : [],
            specialties: data.specialties ? (data.specialties as string).split(', ').filter((s: string) => s.trim()) : [],
            experience_years: (data.experience_years as string) || '',
            year_began_training: (data.year_began_training as string) || '',
            first_10_min_rate: (data.first_10_min_rate as string) || '',
            per_minute_rate: (data.per_minute_rate as string) || '',
            bio: (data.bio as string) || '',
            legal_states: Array.isArray(data.legal_states) ? data.legal_states as string[] :
                        data.legal_states ? (data.legal_states as string).split(',').map((s: string) => s.trim()) : [],
            current_rating: (data.current_rating as number) || 0.0
          };

          setEditFormData(newEditFormData);
        } else {
          console.error('Trainer profile not found:', response.Message);
          setError(`Failed to load trainer profile: ${response.Message}`);
        }
      } else {
        console.error('No trainer ID found in localStorage');
        setError('No trainer ID found. Please complete your profile setup.');
      }
    } catch (err) {
      console.error('Error loading trainer profile:', err);
      setError('Failed to load trainer profile');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTrainingRequests = async (): Promise<void> => {
    try {
      console.log('=== LOADING ACTIVE TRAINING REQUESTS ===');
      await loadTrainerRequests();
    } catch (err) {
      console.error('Error loading active training requests:', err);
    }
  };

  const loadSessionHistory = async (): Promise<void> => {
    try {
      const trainerId = localStorage.getItem('trainerId');
      if (trainerId) {
        const response = await apiService.getTrainerRequests(trainerId) as Record<string, unknown>;
        if (response.Code === 200) {
          const allRequests = (response.requests as TrainerRequest[]) || [];
          const completedRequests = allRequests.filter((req: TrainerRequest) => req.status === 'completed');
          setSessionHistory(completedRequests);

          for (const request of completedRequests) {
            try {
              const deliverablesResponse = await apiService.getDeliverables(request.id) as Record<string, unknown>;
              if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                setDeliverablesData(prev => ({
                  ...prev,
                  [request.id]: deliverablesResponse.data as DeliverablesFormData
                }));
              }
            } catch (err) {
              console.log(`No deliverables found for request ${request.id}`);
            }
          }
        } else {
          setSessionHistory([]);
        }
      }
    } catch (err) {
      console.error('Error loading session history:', err);
      setSessionHistory([]);
    }
  };

  const handleTabChange = (tab: string): void => {
    setActiveTab(tab);
    if (tab === 'requests') {
      loadActiveTrainingRequests();
    } else if (tab === 'history') {
      loadSessionHistory();
    } else if (tab === 'bank') {
      loadConnectStatus();
    }
  };

  const loadConnectStatus = async (): Promise<void> => {
    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) return;
    try {
      setConnectLoading(true);
      const response = await apiService.getConnectAccountStatus(trainerId) as ConnectStatusExtended;
      if (response.Code === 200) {
        setConnectStatus(response);
      }
    } catch (err) {
      console.error('Error loading connect status:', err);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleConnectBankAccount = async (): Promise<void> => {
    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) return;
    try {
      setConnectLoading(true);
      if (!connectStatus?.has_connect_account) {
        const createResponse = await apiService.createConnectAccount(trainerId) as Record<string, unknown>;
        if (createResponse.Code !== 200) {
          alert('Failed to create Connect account: ' + createResponse.Message);
          return;
        }
      }
      const linkResponse = await apiService.createOnboardingLink(trainerId) as Record<string, unknown>;
      if (linkResponse.Code === 200 && linkResponse.url) {
        window.location.href = linkResponse.url as string;
      } else {
        alert('Failed to create onboarding link: ' + linkResponse.Message);
      }
    } catch (err) {
      console.error('Error starting Connect onboarding:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleEditProfile = (): void => {
    setEditingProfile(true);
  };

  const handleSaveProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      const trainerId = localStorage.getItem('trainerId');

      let fullBusinessAddress = editFormData.business_street;
      if (editFormData.business_apt) {
        fullBusinessAddress += ', ' + editFormData.business_apt;
      }
      if (editFormData.business_city) {
        fullBusinessAddress += ', ' + editFormData.business_city;
      }
      if (editFormData.business_state) {
        fullBusinessAddress += ', ' + editFormData.business_state;
      }
      if (editFormData.business_zip) {
        fullBusinessAddress += ' ' + editFormData.business_zip;
      }

      const updateData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        phone_number: editFormData.phone_number,
        business_name: editFormData.business_name,
        time_zone: editFormData.time_zone,
        business_address: fullBusinessAddress,
        credentials: editFormData.credentials,
        training_philosophy: editFormData.training_philosophy,
        areas_of_expertise: editFormData.areas_of_expertise,
        experience_years: editFormData.experience_years,
        year_began_training: editFormData.year_began_training,
        first_10_min_rate: parseFloat(parseFloat(editFormData.first_10_min_rate || '0').toFixed(2)),
        per_minute_rate: parseFloat(parseFloat(editFormData.per_minute_rate || '0').toFixed(2)),
        bio: editFormData.bio,
        legal_states: editFormData.legal_states
      };

      const response = await apiService.updateTrainerProfile(trainerId as string, updateData) as Record<string, unknown>;

      if (response.Code === 200) {
        setTrainerProfile({
          ...trainerProfile,
          ...editFormData
        });
        setEditingProfile(false);
        setError(null);
      } else {
        setError((response.Message as string) || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    const trainerId = localStorage.getItem('trainerId');
    if (!trainerId) {
      setError('Trainer ID not found');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      const response = await apiService.uploadTrainerProfilePhoto(trainerId, file) as Record<string, unknown>;

      if (response.Code === 200) {
        await loadTrainerProfile();
        alert('Profile photo uploaded successfully!');
      } else {
        setError((response.Message as string) || 'Failed to upload photo');
      }
    } catch (err: unknown) {
      console.error('Photo upload error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload photo';
      setError(errorMessage);
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleCancelEdit = (): void => {
    setEditingProfile(false);

    if (trainerProfile) {
      const addressParts = trainerProfile.address ? (trainerProfile.address as string).split(', ') : ['', '', ''];
      const streetAddress = addressParts[0] || '';
      const stateZip = addressParts[1] || '';
      const stateZipParts = stateZip.split(' ');
      const state = stateZipParts[0] || '';
      const zipCode = stateZipParts[1] || '';

      const specialtiesArray = trainerProfile.specialties ?
        (trainerProfile.specialties as string).split(', ').filter((s: string) => s.trim()) : [];

      setEditFormData({
        first_name: (trainerProfile.first_name as string) || '',
        last_name: (trainerProfile.last_name as string) || '',
        email: (trainerProfile.email as string) || '',
        phone_number: (trainerProfile.phone_number as string) || '',
        address: streetAddress,
        state: state,
        zip_code: zipCode,
        specialties: specialtiesArray,
        experience_years: (trainerProfile.experience_years as string) || '',
        hourly_rate: (trainerProfile.hourly_rate as string) || '',
        bio: (trainerProfile.bio as string) || '',
        business_name: (trainerProfile.business_name as string) || '',
        time_zone: (trainerProfile.time_zone as string) || '',
        business_address: (trainerProfile.business_address as string) || '',
        business_street: '',
        business_apt: '',
        business_city: '',
        business_state: '',
        business_zip: '',
        credentials: (trainerProfile.credentials as string[]) || [],
        training_philosophy: (trainerProfile.training_philosophy as string[]) || [],
        areas_of_expertise: (trainerProfile.areas_of_expertise as string[]) || [],
        year_began_training: (trainerProfile.year_began_training as string) || '',
        first_10_min_rate: (trainerProfile.first_10_min_rate as string) || '',
        per_minute_rate: (trainerProfile.per_minute_rate as string) || '',
        legal_states: (trainerProfile.legal_states as string[]) || [],
        current_rating: (trainerProfile.current_rating as number) || 0.0
      });
    }
  };

  const handleInputChange = (field: string, value: string | string[]): void => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSpecialtyToggle = (specialty: string): void => {
    setEditFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter((s: string) => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleCredentialToggle = (credential: string): void => {
    setEditFormData(prev => ({
      ...prev,
      credentials: prev.credentials.includes(credential)
        ? prev.credentials.filter((c: string) => c !== credential)
        : [...prev.credentials, credential]
    }));
  };

  const handleTrainingPhilosophyToggle = (philosophy: string): void => {
    setEditFormData(prev => ({
      ...prev,
      training_philosophy: prev.training_philosophy.includes(philosophy)
        ? prev.training_philosophy.filter((p: string) => p !== philosophy)
        : [...prev.training_philosophy, philosophy]
    }));
  };

  const handleAreaOfExpertiseToggle = (area: string): void => {
    setEditFormData(prev => ({
      ...prev,
      areas_of_expertise: prev.areas_of_expertise.includes(area)
        ? prev.areas_of_expertise.filter((a: string) => a !== area)
        : [...prev.areas_of_expertise, area]
    }));
  };

  const handleAcceptSession = async (requestId: string): Promise<void> => {
    try {
      setLoading(true);
      const response = await apiService.acceptSession(requestId) as Record<string, unknown>;

      if (response.Code === 200) {
        await loadTrainerRequests();
        const statusMessage = response.status === 'awaiting_payment'
          ? `Session accepted. Waiting for client payment of $${Number(response.total_price || 0).toFixed(2)}.`
          : 'Session accepted. You can join the session when you are ready.';
        alert(statusMessage);
      } else {
        setError((response.Message as string) || 'Failed to accept session');
      }
    } catch (err) {
      console.error('Error accepting session:', err);
      setError('Failed to accept session');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = (requestId: string): void => {
    setLoading(true);
    (apiService.registerSessionJoin(requestId, 'trainer') as Promise<Record<string, unknown>>)
      .then((response: Record<string, unknown>) => {
        if (response.Code === 200) {
          window.location.href = `/video-call?request_id=${requestId}`;
        } else {
          setError((response.Message as string) || 'Failed to join session');
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        console.error('Error registering trainer join:', err);
        setError('Failed to join session');
        setLoading(false);
      });
  };

  const handleAcceptingRequestsToggle = async (): Promise<void> => {
    try {
      setToggleLoading(true);
      const trainerId = localStorage.getItem('trainerId');

      if (!trainerId) {
        setError('No trainer ID found');
        return;
      }

      if (acceptingRequests) {
        console.log('Making trainer inactive...');
        const response = await apiService.makeTrainerInactive(trainerId) as Record<string, unknown>;

        if (response.Code === 200) {
          setAcceptingRequests(false);
          setError(null);
        } else {
          setError((response.Message as string) || 'Failed to make trainer inactive');
        }
      } else {
        console.log('Making trainer active...');
        const response = await apiService.makeTrainerActive(trainerId) as Record<string, unknown>;

        if (response.Code === 200) {
          setAcceptingRequests(true);
          setError(null);
          await loadTrainerRequests();
        } else {
          setError((response.Message as string) || 'Failed to make trainer active');
        }
      }
    } catch (err) {
      console.error('Error toggling trainer status:', err);
      setError('Failed to update trainer status');
    } finally {
      setToggleLoading(false);
    }
  };

  const renderBankTab = (): React.ReactElement => (
    <div>
      <h2 className="text-2xl text-[#f1f5f9] mb-5">Bank Account</h2>
      {connectLoading ? (
        <div className="flex justify-center py-10">
          <Loader color="blue" />
        </div>
      ) : connectStatus?.onboarding_complete ? (
        <div className="bg-[#d4edda] border border-[#c3e6cb] rounded-[10px] p-4 sm:p-[30px] text-center">
          <div className="text-5xl mb-4">&#x2705;</div>
          <h3 className="text-[#155724] mb-2.5">Bank Account Connected</h3>
          <p className="text-[#155724]">
            Your bank account is connected. Session earnings will be deposited automatically.
          </p>
        </div>
      ) : (
        <div className="bg-[#fff3cd] border border-[#ffc107] rounded-[10px] p-4 sm:p-[30px] text-center">
          <div className="text-5xl mb-4">&#x1F3E6;</div>
          <h3 className="text-[#856404] mb-2.5">
            {connectStatus?.has_connect_account ? 'Complete Your Setup' : 'Connect Your Bank Account'}
          </h3>
          <p className="text-[#856404] mb-5">
            {connectStatus?.has_connect_account
              ? 'Your account setup is incomplete. Please finish onboarding to start receiving payouts.'
              : 'Connect your bank account through Stripe to receive session earnings directly.'}
          </p>
          <Button
            onClick={handleConnectBankAccount}
            disabled={connectLoading}
            className="!bg-[#635bff] !text-white !text-[1.1rem] !font-semibold !px-[30px] !py-[15px] !rounded-lg hover:!opacity-90"
          >
            {connectLoading ? 'Please wait...' : connectStatus?.has_connect_account ? 'Continue Setup' : 'Connect Bank Account'}
          </Button>
        </div>
      )}
    </div>
  );

  const renderProfileTab = (): React.ReactElement => (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[30px] gap-3">
        <h2 className="text-xl sm:text-2xl text-[#f1f5f9] m-0 mb-0 sm:mb-5">My Profile</h2>
        {!editingProfile && (
          <Button
            onClick={handleEditProfile}
            className="!bg-gradient-to-br !from-[#e67e22] !to-[#f39c12] !text-white !border-none !px-5 !py-2.5 !rounded-lg !text-base !font-medium hover:!opacity-90"
          >
            Edit Profile
          </Button>
        )}
      </div>

      {!trainerProfile || !trainerProfile.first_name ? (
        <div className="bg-[#334155] rounded-[10px] p-[25px] shadow-lg border border-[#475569]">
          <div className="text-center p-10">
            <div className="text-3xl mb-5">&#x23F3;</div>
            <p className="text-[#6c757d] text-[1.1rem]">Loading profile data...</p>
          </div>
        </div>
      ) : (
        <div className="bg-[#334155] rounded-[10px] p-[25px] shadow-lg border border-[#475569]">
          {editingProfile ? (
            <div className="flex flex-col gap-5">
              <h2 className="text-2xl text-[#f1f5f9] m-0 mb-5">Edit Profile Information</h2>

              {/* Profile Photo Upload Section */}
              <div className="mb-[30px] p-4 sm:p-6 bg-[#1e293b] rounded-xl border border-[#334155] shadow-md">
                <h3 className="text-[1.3rem] text-[#f1f5f9] mb-6 font-semibold border-b-2 border-[#3b82f6] pb-3 tracking-tight">Profile Photo</h3>
                <div className="text-center mb-5">
                  {trainerProfile.profile_photo_url ? (
                    <img
                      src={trainerProfile.profile_photo_url as string}
                      alt="Profile"
                      className="w-[120px] h-[120px] rounded-full object-cover border-[3px] border-[#e67e22] mb-4"
                    />
                  ) : (
                    <div className="w-[120px] h-[120px] rounded-full bg-[#334155] flex items-center justify-center text-[2.5rem] text-[#64748b] mx-auto mb-4 border-[3px] border-[#475569]">
                      &#x1F464;
                    </div>
                  )}
                  <div>
                    <label className={`inline-block px-4 py-2 bg-gradient-to-br from-[#e67e22] to-[#f39c12] text-white rounded-md text-[0.9rem] font-medium ${uploadingPhoto ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      {uploadingPhoto ? 'Uploading...' : '\uD83D\uDCF7 Change Photo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* Basic Information Section */}
              <div className="mb-[30px] p-4 sm:p-6 bg-[#1e293b] rounded-xl border border-[#334155] shadow-md">
                <h3 className="text-[1.3rem] text-[#f1f5f9] mb-6 font-semibold border-b-2 border-[#3b82f6] pb-3 tracking-tight">Basic Information</h3>
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                      First Name <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                    </label>
                    <TextInput
                      value={editFormData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      placeholder="Enter your first name"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                      Last Name <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                    </label>
                    <TextInput
                      value={editFormData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      placeholder="Enter your last name"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                    Email Address <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.email}
                    readOnly
                    disabled
                    classNames={{ input: '!bg-[#1e293b] !text-[#94a3b8] !border-[#475569] !rounded-lg !cursor-not-allowed' }}
                  />
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                    Business Name (if applicable)
                  </label>
                  <TextInput
                    value={editFormData.business_name}
                    onChange={(e) => handleInputChange('business_name', e.target.value)}
                    placeholder="e.g., Pawsitive Training Solutions"
                    classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                  />
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                    Phone Number <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                  </label>
                  <TextInput
                    value={editFormData.phone_number}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    placeholder="(555) 123-4567"
                    required
                    classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                  />
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                    Time Zone <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                  </label>
                  <select
                    value={editFormData.time_zone}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('time_zone', e.target.value)}
                    className="p-3 rounded-lg border border-[#475569] bg-[#1e293b] text-[#f1f5f9] text-base cursor-pointer transition-all duration-300 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2024%2024%22%20fill=%22none%22%20stroke=%22%23f1f5f9%22%20stroke-width=%222%22%20stroke-linecap=%22round%22%20stroke-linejoin=%22round%22%3e%3cpolyline%20points=%226,9%2012,15%2018,9%22%3e%3c/polyline%3e%3c/svg%3e')] bg-no-repeat bg-[right_15px_center] bg-[length:16px] pr-[45px]"
                    required
                  >
                    <option value="" className="bg-white text-[#495057] p-2.5">Select your time zone...</option>
                    {timeZoneOptions.map((zone: string) => (
                      <option key={zone} value={zone} className="bg-white text-[#495057] p-2.5">{zone}</option>
                    ))}
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                    Business Address / Mailing Address <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                  </label>
                </div>
                <div className="flex-1 flex flex-col gap-2 mt-2">
                  <label className="text-sm text-[#cbd5e1] font-medium mb-2 block">Street Address <span className="text-[#e74c3c] text-[0.9rem]">*</span></label>
                  <TextInput
                    value={editFormData.business_street}
                    onChange={(e) => handleInputChange('business_street', e.target.value)}
                    placeholder="123 Main Street"
                    required
                    classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2 mt-2">
                  <label className="text-sm text-[#cbd5e1] font-medium mb-2 block">Apt, Ste, etc.</label>
                  <TextInput
                    value={editFormData.business_apt}
                    onChange={(e) => handleInputChange('business_apt', e.target.value)}
                    placeholder="Apt 4B"
                    classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full mt-2">
                  <div className="flex-[2] flex flex-col gap-2">
                    <label className="text-sm text-[#cbd5e1] font-medium mb-2 block">City <span className="text-[#e74c3c] text-[0.9rem]">*</span></label>
                    <TextInput
                      value={editFormData.business_city}
                      onChange={(e) => handleInputChange('business_city', e.target.value)}
                      placeholder="City"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                  <div className="flex-1 mr-2.5 flex flex-col gap-2">
                    <label className="text-sm text-[#cbd5e1] font-medium mb-2 block">State <span className="text-[#e74c3c] text-[0.9rem]">*</span></label>
                    <TextInput
                      value={editFormData.business_state}
                      onChange={(e) => handleInputChange('business_state', e.target.value)}
                      placeholder="State"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-sm text-[#cbd5e1] font-medium mb-2 block">ZIP Code <span className="text-[#e74c3c] text-[0.9rem]">*</span></label>
                    <TextInput
                      value={editFormData.business_zip}
                      onChange={(e) => handleInputChange('business_zip', e.target.value)}
                      placeholder="ZIP"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                </div>
              </div>

              {/* Credentials & Philosophy Section */}
              <div className="mb-[30px] p-4 sm:p-6 bg-[#1e293b] rounded-xl border border-[#334155] shadow-md">
                <h3 className="text-[1.3rem] text-[#f1f5f9] mb-6 font-semibold border-b-2 border-[#3b82f6] pb-3 tracking-tight">Credentials & Training Philosophy</h3>

                <div className="flex-1 flex flex-col gap-2 mb-8">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">Credentials</label>
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-[#334155] rounded-lg bg-[#0f172a]">
                      {credentialOptions.map((cred: string) => (
                        <div
                          key={cred}
                          onClick={() => handleCredentialToggle(cred)}
                          className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                            editFormData.credentials.includes(cred)
                              ? 'bg-[#3b82f6] text-white border-2 border-[#3b82f6] scale-[1.02]'
                              : 'bg-[#1e293b] text-[#f1f5f9] border-[#475569]'
                          }`}
                        >
                          {cred}
                          {editFormData.credentials.includes(cred) && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">&#x2713;</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-2 mb-8">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">Training Philosophy</label>
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-[#334155] rounded-lg bg-[#0f172a]">
                      {trainingPhilosophyOptions.map((phil: string) => (
                        <div
                          key={phil}
                          onClick={() => handleTrainingPhilosophyToggle(phil)}
                          className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                            editFormData.training_philosophy.includes(phil)
                              ? 'bg-[#3b82f6] text-white border-2 border-[#3b82f6] scale-[1.02]'
                              : 'bg-[#1e293b] text-[#f1f5f9] border-[#475569]'
                          }`}
                        >
                          {phil}
                          {editFormData.training_philosophy.includes(phil) && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">&#x2713;</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-2">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">Areas of Expertise</label>
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-[#334155] rounded-lg bg-[#0f172a]">
                      {areasOfExpertiseOptions.map((area: string) => (
                        <div
                          key={area}
                          onClick={() => handleAreaOfExpertiseToggle(area)}
                          className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                            editFormData.areas_of_expertise.includes(area)
                              ? 'bg-[#3b82f6] text-white border-2 border-[#3b82f6] scale-[1.02]'
                              : 'bg-[#1e293b] text-[#f1f5f9] border-[#475569]'
                          }`}
                        >
                          {area}
                          {editFormData.areas_of_expertise.includes(area) && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">&#x2713;</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Rate & Experience Section */}
              <div className="mb-[30px] p-4 sm:p-6 bg-[#1e293b] rounded-xl border border-[#334155] shadow-md">
                <h3 className="text-[1.3rem] text-[#f1f5f9] mb-6 font-semibold border-b-2 border-[#3b82f6] pb-3 tracking-tight">Experience & Rates</h3>
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                      Years of Experience <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                    </label>
                    <TextInput
                      type="number"
                      value={editFormData.experience_years}
                      onChange={(e) => handleInputChange('experience_years', e.target.value)}
                      min={0}
                      max={50}
                      placeholder="5"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                      Year Began Training
                    </label>
                    <TextInput
                      type="number"
                      value={editFormData.year_began_training}
                      onChange={(e) => handleInputChange('year_began_training', e.target.value)}
                      placeholder="2015"
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-5 mt-4">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                      First 10 Minutes Rate ($) <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                      <span className="text-[0.85rem] text-[#94a3b8] ml-2">(Minimum: $10.00)</span>
                    </label>
                    <TextInput
                      value={editFormData.first_10_min_rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleInputChange('first_10_min_rate', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value === '') {
                          handleInputChange('first_10_min_rate', '');
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          const validatedValue = Math.max(10.00, numValue);
                          const formatted = parseFloat(validatedValue.toFixed(2));
                          handleInputChange('first_10_min_rate', formatted.toString());
                        }
                      }}
                      placeholder="15.00"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                    {editFormData.first_10_min_rate && parseFloat(editFormData.first_10_min_rate) < 10 && (
                      <div className="text-[#ef4444] text-[0.85rem] mt-1">
                        Minimum rate is $10.00
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                      Per Minute Rate ($) <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                      <span className="text-[0.85rem] text-[#94a3b8] ml-2">(Minimum: $1.00)</span>
                    </label>
                    <TextInput
                      value={editFormData.per_minute_rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleInputChange('per_minute_rate', value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value === '') {
                          handleInputChange('per_minute_rate', '');
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          const validatedValue = Math.max(1.00, numValue);
                          const formatted = parseFloat(validatedValue.toFixed(2));
                          handleInputChange('per_minute_rate', formatted.toString());
                        }
                      }}
                      placeholder="1.00"
                      required
                      classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg' }}
                    />
                    {editFormData.per_minute_rate && parseFloat(editFormData.per_minute_rate) < 1 && (
                      <div className="text-[#ef4444] text-[0.85rem] mt-1">
                        Minimum rate is $1.00 per minute
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">Specialties</label>
                  <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 max-h-[200px] overflow-y-auto p-3 border border-[#334155] rounded-lg bg-[#0f172a]">
                      {areasOfExpertiseOptions.map((specialty: string) => (
                        <div
                          key={specialty}
                          onClick={() => handleSpecialtyToggle(specialty)}
                          className={`px-3 py-2 border rounded-[20px] cursor-pointer transition-all duration-300 text-[0.9rem] text-center relative select-none ${
                            editFormData.specialties.includes(specialty)
                              ? 'bg-[#3b82f6] text-white border-2 border-[#3b82f6] scale-[1.02]'
                              : 'bg-[#1e293b] text-[#f1f5f9] border-[#475569]'
                          }`}
                        >
                          {specialty}
                          {editFormData.specialties.includes(specialty) && (
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.8rem] font-bold">&#x2713;</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {editFormData.specialties.length > 0 && (
                      <div className="p-2.5 bg-[#1e293b] rounded-lg text-[#f1f5f9] text-[0.9rem]">
                        <strong>Selected: </strong>
                        {editFormData.specialties.join(', ')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 flex flex-col gap-2 mt-4">
                  <label className="font-semibold text-[#f1f5f9] text-[0.9rem]">
                    Bio <span className="text-[#e74c3c] text-[0.9rem]">*</span>
                  </label>
                  <Textarea
                    value={editFormData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    placeholder="Tell clients about your training approach, certifications, experience, and what makes you special as a dog trainer..."
                    required
                    minRows={4}
                    autosize
                    classNames={{ input: '!bg-[#1e293b] !text-[#f1f5f9] !border-[#475569] !rounded-lg !min-h-[100px]' }}
                  />
                </div>

                <div className="bg-[#1e293b] border border-[#475569] rounded-lg p-4 mt-5">
                  <div className="text-base font-semibold text-[#fbbf24] mb-2">&#x1F4A1; Pricing Information</div>
                  <div className="text-[0.9rem] text-[#cbd5e1] leading-relaxed">
                    Your hourly rate will be displayed as: <strong>$15.00 first 10 minutes + ${editFormData.hourly_rate ? (Number(editFormData.hourly_rate) / 60).toFixed(2) : '0.00'}/min after</strong><br/>
                    This pricing structure encourages quick, focused sessions while providing fair compensation for longer training sessions.
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-5">
                <Button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="!bg-gradient-to-br !from-[#27ae60] !to-[#2ecc71] !text-white !border-none !px-6 !py-3 !rounded-lg !text-base !font-medium hover:!opacity-90"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  className="!bg-transparent !text-[#6c757d] !border-2 !border-[#6c757d] !px-6 !py-2.5 !rounded-lg !text-base !font-medium hover:!bg-[#6c757d] hover:!text-white"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {/* Profile Photo Section */}
              <div className="mb-[30px] text-center">
                <div className="relative inline-block">
                  {trainerProfile.profile_photo_url ? (
                    <img
                      src={trainerProfile.profile_photo_url as string}
                      alt="Profile"
                      className="w-[150px] h-[150px] rounded-full object-cover border-4 border-[#e67e22] mb-4"
                    />
                  ) : (
                    <div className="w-[150px] h-[150px] rounded-full bg-[#334155] flex items-center justify-center text-5xl text-[#64748b] mx-auto mb-4 border-4 border-[#475569]">
                      &#x1F464;
                    </div>
                  )}
                  <div>
                    <label className={`inline-block px-4 py-2 bg-gradient-to-br from-[#e67e22] to-[#f39c12] text-white rounded-md text-[0.9rem] font-medium ${uploadingPhoto ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      {uploadingPhoto ? 'Uploading...' : '\uD83D\uDCF7 Upload Photo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-5 border-b-2 border-white/20 gap-2">
                <h3 className="text-[1.8rem] text-white m-0">
                  {trainerProfile.first_name as string} {trainerProfile.last_name as string}
                </h3>
                <div className="flex items-center gap-2 text-white font-medium">
                  <span className="w-2.5 h-2.5 bg-[#27ae60] rounded-full"></span>
                  Active Trainer
                </div>
                {(trainerProfile.current_rating as number) > 0 && (
                  <div className="mt-2.5 flex items-center gap-2.5">
                    <span className="text-[0.9rem] text-white font-medium">Current Rating:</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star: number) => (
                        <span
                          key={star}
                          className={`text-[1.2rem] transition-colors duration-200 ${star <= (trainerProfile.current_rating as number) ? 'text-[#f39c12]' : 'text-[#ddd]'}`}
                        >
                          &#x2605;
                        </span>
                      ))}
                      <span className="ml-2 text-base font-semibold text-[#2c3e50]">
                        {(trainerProfile.current_rating as number).toFixed(1)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Email:</span>
                  <span className="text-white flex-1">{trainerProfile.email as string}</span>
                </div>
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Phone:</span>
                  <span className="text-white flex-1">{trainerProfile.phone_number as string}</span>
                </div>
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Address:</span>
                  <span className="text-white flex-1">{trainerProfile.address as string}</span>
                </div>
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Specialties:</span>
                  <span className="text-white flex-1">{trainerProfile.specialties as string}</span>
                </div>
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Experience:</span>
                  <span className="text-white flex-1">{trainerProfile.experience_years as string} years</span>
                </div>
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Hourly Rate:</span>
                  <span className="text-white flex-1">${trainerProfile.hourly_rate as string}/hour</span>
                </div>
                <div className="flex gap-5">
                  <span className="font-semibold text-white min-w-[120px]">Bio:</span>
                  <span className="text-white flex-1">{trainerProfile.bio as string}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderRequestsTab = (): React.ReactElement => (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[30px] gap-3">
        <h2 className="text-xl sm:text-2xl text-[#f1f5f9] m-0 mb-0 sm:mb-5">Active Training Requests</h2>
        <div className="flex flex-wrap gap-4 items-center">
          <Button
            onClick={loadTrainerRequests}
            disabled={requestsLoading}
            className={`!px-4 !py-2 !bg-[#334155] !text-[#cbd5e1] !border !border-[#475569] !rounded-md !text-[0.9rem] !font-medium hover:!bg-[#475569] ${requestsLoading ? '!bg-[#475569] !cursor-wait !opacity-70' : ''}`}
          >
            {requestsLoading ? 'Refreshing...' : '\uD83D\uDD04 Refresh'}
          </Button>
          <div className="flex items-center gap-4">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acceptingRequests}
              onChange={handleAcceptingRequestsToggle}
              disabled={toggleLoading}
              className="hidden"
            />
            <span className={`relative w-[60px] h-[30px] rounded-[30px] transition-all duration-300 cursor-pointer ${acceptingRequests ? 'bg-[#27ae60]' : 'bg-[#ccc]'} ${toggleLoading ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <span className={`absolute top-[3px] left-[3px] w-6 h-6 bg-white rounded-full transition-all duration-300 shadow-md ${acceptingRequests ? 'translate-x-[30px]' : 'translate-x-0'}`}></span>
            </span>
            <span className="text-base font-medium text-[#f1f5f9] min-w-[150px]">
              {toggleLoading ? 'Updating...' : acceptingRequests ? 'Accepting Requests' : 'Not Accepting Requests'}
            </span>
          </label>
        </div>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        {acceptingRequests ? (
          <div>
            <h3 className="text-[1.3rem] text-[#f1f5f9] mb-6 font-semibold border-b-2 border-[#3b82f6] pb-3 tracking-tight">Session Requests</h3>
            {requestsLoading ? (
              <div className="text-center text-[#94a3b8] py-10 text-base">
                <Loader color="blue" className="mx-auto mb-4" />
                Loading requests...
              </div>
            ) : trainerRequests.length === 0 ? (
              <div className="text-center py-[60px] px-5 text-[#cbd5e1]">
                <div className="text-6xl mb-5 text-[#64748b]">No Requests</div>
                <h3 className="text-2xl text-[#f1f5f9] mb-2.5">No Session Requests</h3>
                <p className="text-base text-[#cbd5e1]">
                  You don't have any session requests yet. Clients will be able to request sessions with you while you're accepting requests.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 max-h-[600px] overflow-y-auto pr-2 mt-5">
                {trainerRequests.filter((req: TrainerRequest) => req.status !== 'completed').map((request: TrainerRequest, index: number) => {
                  const statusLabelMap: Record<string, string> = {
                    pending: 'Awaiting Your Response',
                    awaiting_payment: 'Waiting for Client Payment',
                    accepted: 'Ready to Join',
                    active: 'In Progress',
                    declined: 'Declined',
                    cancelled: 'Cancelled'
                  };
                  const statusLabel = statusLabelMap[request.status] || (request.status ? request.status.charAt(0).toUpperCase() + request.status.slice(1) : 'Unknown');

                  return (
                  <div key={request.id || index} className="bg-[#334155] rounded-xl p-5 border border-[#475569] shadow-md transition-all duration-200 relative min-h-[280px] flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-2xl font-bold text-[#3b82f6]">#{index + 1}</div>
                      <div className="text-xs text-[#94a3b8] font-medium">
                        {new Date(request.requested_at).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="mb-3">
                      <span className={`px-2.5 py-1 rounded-xl text-xs font-semibold uppercase tracking-wider ${getStatusClasses(request.status)}`}>
                        {statusLabel}
                      </span>
                    </div>

                    <div className="flex-1 flex flex-col gap-2">
                      <div className="text-base text-[#f1f5f9] mb-2">
                        <strong>{request.client_username || 'Unknown Client'}</strong>
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="text-sm text-[#cbd5e1] font-medium">{request.pet_name || 'Unknown Pet'}</div>
                        <div className="text-xs text-[#94a3b8] italic">
                          {request.pet_breed && `${request.pet_breed}`}
                          {request.pet_age && ` \u2022 ${request.pet_age} years old`}
                        </div>
                      </div>

                      <div className="text-xs text-[#94a3b8] mt-2 overflow-hidden text-ellipsis">
                        {request.explanation || 'No explanation provided'}
                      </div>
                      {request.duration_minutes && (
                        <div className="text-xs text-[#94a3b8] mt-2 overflow-hidden text-ellipsis">
                          <strong>Duration:</strong> {request.duration_minutes} minutes
                        </div>
                      )}
                      {request.total_price !== undefined && request.total_price !== null && (
                        <div className="text-xs text-[#94a3b8] mt-2 overflow-hidden text-ellipsis">
                          <strong>Session Cost:</strong> ${Number(request.total_price || 0).toFixed(2)}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 mt-3 flex-wrap">
                      {request.status === 'pending' && (
                        <>
                          <Button
                            className="!bg-[#22c55e] !text-white !border-none !px-4 !py-2 !rounded-md !text-sm !font-medium !flex-1 hover:!bg-[#16a34a]"
                            onClick={() => handleAcceptSession(request.id)}
                          >
                            Accept
                          </Button>
                          <Button
                            className="!bg-[#ef4444] !text-white !border-none !px-4 !py-2 !rounded-md !text-sm !font-medium !flex-1 hover:!bg-[#dc2626]"
                            onClick={async () => {
                              try {
                                const response = await apiService.declineSession(request.id) as Record<string, unknown>;
                                if (response.Code === 200) {
                                  await loadTrainerRequests();
                                }
                              } catch (err) {
                                console.error('Error declining session:', err);
                              }
                            }}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                      {(request.status === 'accepted' || request.status === 'active' || (request.status === 'awaiting_payment' && request.payment_status === 'completed')) && (
                        <Button
                          className="!bg-[#3b82f6] !text-white !border-none !px-4 !py-2 !rounded-md !text-sm !font-medium !flex-1 hover:!bg-[#2563eb]"
                          onClick={() => handleJoinSession(request.id)}
                        >
                          {request.status === 'active' ? 'Rejoin Session' : 'Join Session'}
                        </Button>
                      )}
                      {request.status === 'awaiting_payment' && request.payment_status !== 'completed' && (
                        <span className="text-[#64748b] text-xs italic px-4 py-2">Awaiting client payment</span>
                      )}
                      {request.status === 'declined' && (
                        <span className="text-[#64748b] text-xs italic px-4 py-2">Declined</span>
                      )}
                      {request.status === 'cancelled' && (
                        <span className="text-[#64748b] text-xs italic px-4 py-2">Cancelled</span>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-[60px] px-5 text-[#cbd5e1]">
            <div className="text-6xl mb-5 text-[#64748b]">&#x1F512;</div>
            <h3 className="text-2xl text-[#f1f5f9] mb-2.5">Not Accepting Requests</h3>
            <p className="text-base text-[#cbd5e1]">
              Turn on the "Accepting Requests" toggle above to start receiving session requests from clients.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderHistoryTab = (): React.ReactElement => (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-[30px] gap-3">
        <h2 className="text-xl sm:text-2xl text-[#f1f5f9] m-0 mb-0 sm:mb-5">Session History</h2>
        <Button
          onClick={loadSessionHistory}
          disabled={loading}
          className={`!px-4 !py-2 !bg-[#334155] !text-[#cbd5e1] !border !border-[#475569] !rounded-md !text-[0.9rem] !font-medium hover:!bg-[#475569] ${loading ? '!bg-[#475569] !cursor-wait !opacity-70' : ''}`}
        >
          &#x1F504; Refresh
        </Button>
      </div>
      <div className="flex flex-col gap-5">
        {loading ? (
          <div className="text-center text-[#94a3b8] py-10 text-base">
            <Loader color="blue" className="mx-auto mb-4" />
            Loading session history...
          </div>
        ) : sessionHistory.length === 0 ? (
          <div className="text-center py-[60px] px-5 text-[#cbd5e1]">
            <div className="text-6xl mb-5 text-[#64748b]">No History</div>
            <h3 className="text-2xl text-[#f1f5f9] mb-2.5">No Session History</h3>
            <p className="text-base text-[#cbd5e1]">
              Your completed training sessions will appear here.
            </p>
          </div>
        ) : (
          sessionHistory.map((request: TrainerRequest, index: number) => {
            const clientName = request.client_username || request.client_name || 'Unknown Client';
            const sessionDate = request.accepted_at || request.requested_at;
            const formattedDate = sessionDate ? new Date(sessionDate).toLocaleDateString() : 'N/A';
            const formattedTime = sessionDate ? new Date(sessionDate).toLocaleTimeString() : 'N/A';

            return (
              <div key={request.id || index} className="bg-[#334155] rounded-lg px-3 sm:px-5 py-4 border border-[#475569] mb-3 transition-all duration-300 shadow-md">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 w-full">
                  <div className="min-w-[150px]">
                    <div className="text-base font-semibold text-[#f1f5f9] mb-1">Session #{request.id?.slice(-8) || index}</div>
                    <div className="text-[0.85rem] text-[#94a3b8]">{formattedDate}</div>
                  </div>
                  <div className="flex-1 flex items-center flex-wrap text-[0.9rem]">
                    <span className="text-[0.9rem] text-[#94a3b8] font-medium">Client: </span>
                    <span className="text-[0.9rem] text-[#f1f5f9] font-medium">{clientName}</span>
                    <span className="text-[#475569] mx-2"> | </span>
                    <span className="text-[0.9rem] text-[#94a3b8] font-medium">Duration: </span>
                    <span className="text-[0.9rem] text-[#f1f5f9] font-medium">{request.duration_minutes || 0} minutes</span>
                    <span className="text-[#475569] mx-2"> | </span>
                    <span className="text-[0.9rem] text-[#94a3b8] font-medium">Time: </span>
                    <span className="text-[0.9rem] text-[#f1f5f9] font-medium">{formattedTime}</span>
                    {request.pet_name && (
                      <>
                        <span className="text-[#475569] mx-2"> | </span>
                        <span className="text-[0.9rem] text-[#94a3b8] font-medium">Pet: </span>
                        <span className="text-[0.9rem] text-[#f1f5f9] font-medium">{request.pet_name}</span>
                      </>
                    )}
                    {request.total_price !== undefined && request.total_price !== null && (
                      <>
                        <span className="text-[#475569] mx-2"> | </span>
                        <span className="text-[0.9rem] text-[#94a3b8] font-medium">Price: </span>
                        <span className="text-[0.9rem] text-[#f1f5f9] font-medium">${Number(request.total_price || 0).toFixed(2)}</span>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-2.5 items-start sm:items-end">
                    <span className="bg-[#10b981] text-white px-3 py-1.5 rounded-md text-[0.85rem] font-medium">Completed</span>
                    <Button
                      className="!px-4 !py-2 !bg-[#3b82f6] !text-white !border-none !rounded-md !text-[0.9rem] !font-medium hover:!bg-[#2563eb]"
                      onClick={async () => {
                        setSelectedDeliverablesRequest(request);
                        if (!deliverablesData[request.id]) {
                          try {
                            const deliverablesResponse = await apiService.getDeliverables(request.id) as Record<string, unknown>;
                            if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                              setDeliverablesData(prev => ({
                                ...prev,
                                [request.id]: deliverablesResponse.data as DeliverablesFormData
                              }));
                            } else {
                              setDeliverablesData(prev => ({
                                ...prev,
                                [request.id]: {
                                  summary: '',
                                  checklist_items: []
                                }
                              }));
                            }
                          } catch (err) {
                            console.error('Error loading deliverables:', err);
                            setDeliverablesData(prev => ({
                              ...prev,
                              [request.id]: {
                                summary: '',
                                checklist_items: []
                              }
                            }));
                          }
                        }
                      }}
                    >
                      {deliverablesData[request.id] ? 'Edit Deliverables' : 'Add Deliverables'}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Deliverables Modal */}
      <Modal
        opened={!!selectedDeliverablesRequest}
        onClose={() => setSelectedDeliverablesRequest(null)}
        title="Session Deliverables"
        size="lg"
        classNames={{
          content: '!bg-[#1e293b] !border !border-[#334155]',
          header: '!bg-[#1e293b] !border-b !border-[#334155]',
          title: '!text-2xl !font-semibold !text-[#f1f5f9]',
          close: '!text-[#94a3b8] hover:!text-white',
          body: '!bg-[#1e293b] !p-6'
        }}
      >
        {selectedDeliverablesRequest && (
          <>
            <div className="mb-5">
              <label className="block text-[0.9rem] font-medium text-[#cbd5e1] mb-2">
                Summary <span className="text-[#e74c3c] text-[0.9rem]">*</span>
              </label>
              <Textarea
                value={deliverablesData[selectedDeliverablesRequest.id]?.summary || ''}
                onChange={(e) => handleDeliverablesInputChange(selectedDeliverablesRequest.id, 'summary', e.target.value)}
                placeholder="Enter a short summary of the main points covered in this session..."
                minRows={6}
                autosize
                classNames={{ input: '!bg-[#0f172a] !text-[#f1f5f9] !border-[#475569] !rounded-lg !max-h-[300px] !overflow-y-auto' }}
              />
            </div>

            <div className="mb-5">
              <label className="block text-[0.9rem] font-medium text-[#cbd5e1] mb-2">
                Checklist Items
              </label>
              {(deliverablesData[selectedDeliverablesRequest.id]?.checklist_items || []).map((item: string, itemIndex: number) => (
                <div key={itemIndex} className="flex gap-2 mb-2.5 items-center">
                  <Textarea
                    value={item}
                    onChange={(e) => handleChecklistItemChange(selectedDeliverablesRequest.id, itemIndex, e.target.value)}
                    placeholder={`Checklist item ${itemIndex + 1}`}
                    minRows={Math.max(2, Math.ceil(item.length / 50))}
                    autosize
                    className="flex-1"
                    classNames={{ input: '!bg-[#0f172a] !text-[#f1f5f9] !border-[#475569] !rounded-md !min-h-[40px] !overflow-y-auto' }}
                  />
                  <Button
                    onClick={() => handleRemoveChecklistItem(selectedDeliverablesRequest.id, itemIndex)}
                    className="!px-4 !py-2.5 !bg-[#ef4444] !text-white !border-none !rounded-md !text-xl !font-bold !leading-none hover:!bg-[#dc2626]"
                  >
                    &#x00D7;
                  </Button>
                </div>
              ))}
              <Button
                onClick={() => handleAddChecklistItem(selectedDeliverablesRequest.id)}
                className="!px-4 !py-2.5 !bg-[#334155] !text-[#f1f5f9] !border !border-[#475569] !rounded-md !text-[0.9rem] !font-medium hover:!bg-[#475569]"
              >
                + Add Checklist Item
              </Button>
            </div>

            <div className="pt-5 border-t border-[#334155] flex justify-end gap-3">
              <Button
                onClick={() => setSelectedDeliverablesRequest(null)}
                className="!px-5 !py-2.5 !bg-[#475569] !text-[#f1f5f9] !border-none !rounded-md !text-[0.9rem] !font-medium hover:!bg-[#64748b]"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleSaveDeliverables(selectedDeliverablesRequest.id, selectedDeliverablesRequest);
                  setSelectedDeliverablesRequest(null);
                }}
                disabled={savingDeliverables[selectedDeliverablesRequest.id] || !deliverablesData[selectedDeliverablesRequest.id]?.summary}
                className="!px-5 !py-2.5 !bg-[#10b981] !text-white !border-none !rounded-md !text-[0.9rem] !font-medium hover:!bg-[#059669]"
              >
                {savingDeliverables[selectedDeliverablesRequest.id] ? 'Saving...' : 'Save Deliverables'}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );

  if (loading && !trainerProfile) {
    return (
      <div className="font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] bg-[#0f172a] min-h-screen text-[#f1f5f9]">
        <div className="bg-[#1e293b] text-[#f1f5f9] py-5 shadow-[0_1px_0_rgba(0,0,0,0.2)] border-b border-[#334155]">
          <div className="max-w-[1200px] mx-auto px-3 sm:px-5 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4 cursor-pointer">
              <img
                src="/images/logo.png"
                alt="Pheefee Logo"
                className="w-10 h-10 object-contain rounded-lg"
              />
              <span className="text-xl sm:text-2xl font-bold">Trainer Portal</span>
            </div>
          </div>
        </div>
        <div className="text-center py-[60px] px-5">
          <Loader color="blue" size="lg" className="mb-5" />
          <p className="text-[#4a5568] text-[1.1rem]">Loading trainer profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',sans-serif] bg-[#0f172a] min-h-screen text-[#f1f5f9]">
      {error && (
        <div className="bg-[#f8d7da] text-[#721c24] p-4 rounded-lg mb-5 border border-[#f5c6cb] flex items-center">
          {error}
          <button
            onClick={() => setError(null)}
            className="bg-transparent border-none text-[#721c24] ml-4 cursor-pointer text-xl hover:opacity-70"
          >
            &#x00D7;
          </button>
        </div>
      )}

      <header className="bg-[#1e293b] text-[#f1f5f9] py-5 shadow-[0_1px_0_rgba(0,0,0,0.2)] border-b border-[#334155]">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 sm:gap-4 cursor-pointer flex-wrap">
            <Button
              onClick={() => onBackToHome && onBackToHome()}
              variant="outline"
              className="!bg-transparent !border !border-[#475569] !text-[#cbd5e1] !px-3 sm:!px-5 !py-2.5 !rounded-lg !font-medium !text-sm sm:!text-base hover:!bg-[#334155]"
            >
              &#x2190; Back
            </Button>
            <div className="border-l border-white/30 h-[30px] hidden sm:block"></div>
            <img
              src="/images/logo.png"
              alt="Pheefee Logo"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <span className="text-xl sm:text-2xl font-bold">Trainer Portal</span>
          </div>
          {isLoggedIn && onLogout && (
            <Button
              onClick={onLogout}
              variant="outline"
              className="!bg-transparent !border !border-[#475569] !text-[#cbd5e1] !px-5 !py-2.5 !rounded-lg !font-medium !text-base hover:!bg-[#334155]"
            >
              Logout
            </Button>
          )}
        </div>
      </header>

      <div className="bg-[#1e293b] border-b border-[#334155] px-3 sm:px-6">
        <div className="flex gap-1 pt-3 overflow-x-auto">
          <button
            onClick={() => handleTabChange('requests')}
            className={`px-5 py-3 bg-transparent border-none text-[#94a3b8] cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 border-transparent transition-all duration-200 rounded-t-md mr-1 hover:text-white ${activeTab === 'requests' ? '!text-white !border-b-[#3b82f6] !font-semibold !bg-[rgba(59,130,246,0.1)]' : ''}`}
          >
            Active Requests
          </button>
          <button
            onClick={() => handleTabChange('history')}
            className={`px-5 py-3 bg-transparent border-none text-[#94a3b8] cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 border-transparent transition-all duration-200 rounded-t-md mr-1 hover:text-white ${activeTab === 'history' ? '!text-white !border-b-[#3b82f6] !font-semibold !bg-[rgba(59,130,246,0.1)]' : ''}`}
          >
            Session History
          </button>
          <button
            onClick={() => handleTabChange('profile')}
            className={`px-5 py-3 bg-transparent border-none text-[#94a3b8] cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 border-transparent transition-all duration-200 rounded-t-md mr-1 hover:text-white ${activeTab === 'profile' ? '!text-white !border-b-[#3b82f6] !font-semibold !bg-[rgba(59,130,246,0.1)]' : ''}`}
          >
            My Profile
          </button>
          <button
            onClick={() => handleTabChange('earnings')}
            className={`px-5 py-3 bg-transparent border-none text-[#94a3b8] cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 border-transparent transition-all duration-200 rounded-t-md mr-1 hover:text-white ${activeTab === 'earnings' ? '!text-white !border-b-[#3b82f6] !font-semibold !bg-[rgba(59,130,246,0.1)]' : ''}`}
          >
            Earnings
          </button>
          <button
            onClick={() => handleTabChange('bank')}
            className={`px-5 py-3 bg-transparent border-none text-[#94a3b8] cursor-pointer text-sm font-medium whitespace-nowrap border-b-2 border-transparent transition-all duration-200 rounded-t-md mr-1 hover:text-white ${activeTab === 'bank' ? '!text-white !border-b-[#3b82f6] !font-semibold !bg-[rgba(59,130,246,0.1)]' : ''}`}
          >
            Bank Account
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto py-4 sm:py-[30px] px-3 sm:px-5">
        <div className="flex-1 bg-[#1e293b] rounded-[15px] p-4 sm:p-6 md:p-[30px] shadow-lg border border-[#334155] min-h-[600px]">
          {activeTab === 'requests' && renderRequestsTab()}
          {activeTab === 'history' && renderHistoryTab()}
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'earnings' && <TrainerEarningsDashboard trainerId={localStorage.getItem('trainerId') || ''} />}
          {activeTab === 'bank' && renderBankTab()}
        </div>
      </div>
    </div>
  );
}

export default TrainerPortal;
